import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, ChevronDown, Loader2, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePersistedOpen } from "@/lib/usePersistedOpen";

const URL = `${process.env.REACT_APP_BACKEND_URL}/api/admin/maintenance/duplicate-trades`;
const cfg = { withCredentials: true };
const money = (n) => `${n < 0 ? "-" : "+"}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const ScanResult = ({ scan }) => (
  <div className="border border-bt-border-subtle bg-bt-surface-2/40 p-4 text-xs font-mono" data-testid="dup-scan-result">
    {scan.total === 0 ? (
      <p className="text-bt-positive" data-testid="dup-none">No duplicate trades found. Trade history is clean.</p>
    ) : (
      <>
        <p className="text-bt-text-primary mb-2">
          <span className="text-bt-negative font-semibold" data-testid="dup-total">{scan.total.toLocaleString()}</span> duplicate auto-exit trades found
        </p>
        {scan.users.map(u => (
          <div key={u.email} className="flex justify-between py-1 border-t border-bt-border-subtle" data-testid="dup-user-row">
            <span className="text-bt-text-secondary">{u.email}</span>
            <span><span className="text-bt-text-primary">{u.duplicates.toLocaleString()}</span> · <span className="text-bt-negative">{money(u.phantom_pnl)} phantom P&amp;L</span></span>
          </div>
        ))}
      </>
    )}
  </div>
);

export default function DataCleanupPanel() {
  const [open, setOpen] = usePersistedOpen("data_cleanup", false);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(null);

  const run = async (apply) => {
    setBusy(apply ? "clean" : "scan");
    try {
      const { data } = apply ? await axios.post(URL, {}, cfg) : await axios.get(URL, cfg);
      if (apply) {
        toast.success(`Removed ${data.removed.toLocaleString()} duplicate trades`);
        setScan({ total: 0, users: [] });
      } else setScan(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Operation failed"); }
    setBusy(null);
  };

  return (
    <div className="bg-bt-surface border border-bt-border-subtle mb-6" data-testid="admin-data-cleanup">
      <button onClick={() => setOpen(o => !o)} data-testid="toggle-data-cleanup"
        className="w-full px-4 py-3 border-b border-bt-border-subtle flex items-center justify-between hover:bg-bt-surface-2/40 transition-colors">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
          <span className="text-xs font-mono uppercase tracking-wider text-bt-text-secondary">Data Maintenance</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-bt-text-tertiary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-4 space-y-3">
          <p className="text-sm text-bt-text-secondary">
            Finds stop-loss, trailing-stop and drawdown exits that were logged over and over for the same position, each copy within 30 minutes of the last.
            It keeps the first trade in each run and removes the copies. Account balances are not changed.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => run(false)} disabled={!!busy} data-testid="dup-scan-btn"
              className="font-mono text-xs border-bt-border-subtle">
              {busy === "scan" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />} Scan for duplicates
            </Button>
            {scan?.total > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={!!busy} data-testid="dup-clean-btn" className="font-mono text-xs bg-bt-negative text-white hover:bg-bt-negative/80">
                    {busy === "clean" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                    Clean up {scan.total.toLocaleString()} trades
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-mono">Remove {scan.total.toLocaleString()} duplicate trades?</AlertDialogTitle>
                    <AlertDialogDescription className="text-bt-text-tertiary">
                      This permanently deletes the duplicate rows from Woodrow trade history. Consider downloading a backup first.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="dup-clean-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => run(true)} className="bg-bt-negative text-white hover:bg-bt-negative/80" data-testid="dup-clean-confirm">
                      Remove duplicates
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          {scan && <ScanResult scan={scan} />}
        </div>
      )}
    </div>
  );
}
